import { Config } from '~/api/def/endpoints/dashboardEndpoint';

/**
 * Calculates the next available position for a new widget in a dashboard grid
 * @param currentConfig - Array of current widget configurations
 * @returns Object with x and y coordinates for the next widget position
 */
export const calculateNextPosition = (
  currentConfig: Config[],
): { x: number; y: number } => {
  if (currentConfig.length === 0) {
    return { x: 0, y: 0 };
  }

  // Filter out widgets without layout properties
  const widgetsWithLayout = currentConfig.filter(
    (widget) =>
      typeof widget.x === 'number' &&
      typeof widget.y === 'number' &&
      typeof widget.w === 'number' &&
      typeof widget.h === 'number',
  );

  if (widgetsWithLayout.length === 0) {
    return { x: 0, y: 0 };
  }

  // Find the bottommost position
  let maxY = 0;
  widgetsWithLayout.forEach((widget) => {
    const widgetEndY = (widget.y ?? 0) + (widget.h ?? 9);
    if (widgetEndY > maxY) maxY = widgetEndY;
  });

  // Check each row from top to bottom for available space
  for (let rowY = 0; rowY <= maxY; rowY += 9) {
    // Assuming 9 is the standard height
    const widgetsInRow = widgetsWithLayout.filter(
      (widget) => widget.y === rowY,
    );

    // Check if there's space in this row
    let occupiedSpace = 0;
    widgetsInRow.forEach((widget) => {
      occupiedSpace += widget.w ?? 6;
    });

    // If there's space for a 6-width widget in this row
    if (occupiedSpace + 6 <= 12) {
      // Find the rightmost position in this row
      let rightmostX = 0;
      widgetsInRow.forEach((widget) => {
        const widgetEndX = (widget.x ?? 0) + (widget.w ?? 6);
        if (widgetEndX > rightmostX) rightmostX = widgetEndX;
      });

      return { x: rightmostX, y: rowY };
    }
  }

  // If no space found in existing rows, place below
  return { x: 0, y: maxY };
};
